import { type AmbientMood, type SfxId } from './ids.js';
import { playSfx } from './sfx.js';
import { AmbientVoice } from './music/ambient.js';

/** Volumes are 0..1 on each bus; the parent volume cap clamps them app-side. */
export interface AudioVolumes {
  readonly master: number;
  readonly sfx: number;
  readonly music: number;
  /** Reserved for the voice bus (slice P4); silent until then. */
  readonly voice: number;
}

/** What callers get. The no-op engine answers the same calls and does nothing. */
export interface AudioEngine {
  /** Call from a user gesture. Nothing sounds before the first unlock, ever. */
  unlock(): void;
  setVolumes(v: AudioVolumes): void;
  setMuted(muted: boolean): void;
  /** Sensory-friendly preset: softer SFX ceiling, no ambience (spec §Settings). */
  setSensoryFriendly(on: boolean): void;
  play(id: SfxId, pan?: number): void;
  startAmbient(mood: AmbientMood): void;
  stopAmbient(): void;
  /** Pause everything when the tab hides; resume when it returns (only if unlocked). */
  handleVisibility(visible: boolean): void;
}

/** The engine's tunables, injected so tests drive a fake context and a deterministic RNG. */
export interface EngineOptions {
  readonly createContext: () => BaseAudioContext;
  readonly random?: () => number;
}

export const NOOP_ENGINE: AudioEngine = {
  unlock: () => {},
  setVolumes: () => {},
  setMuted: () => {},
  setSensoryFriendly: () => {},
  play: () => {},
  startAmbient: () => {},
  stopAmbient: () => {},
  handleVisibility: () => {},
};

const SFX_CEILING = 0.9;
const SENSORY_CEILING = 0.45;

/**
 * The real engine. One `AudioContext`, three buses under a limiter: master → sfx / music / voice.
 * A limiter (not clipping) on master keeps any combination of overlapping sounds under the ceiling
 * the parent set — never sudden (spec §Sound effects).
 */
export function createAudioEngine(opts: EngineOptions): AudioEngine {
  let ctx: BaseAudioContext | null = null;
  // `resume`/`suspend` live on AudioContext, not the base; the voices only need the base, but the
  // engine needs the controls. Captured once, at creation.
  let controls: { resume?: () => Promise<void>; suspend?: () => Promise<void> } = {};
  let master: GainNode | null = null;
  let sfxBus: GainNode | null = null;
  let musicBus: GainNode | null = null;
  let voiceBus: GainNode | null = null;
  let ambient: AmbientVoice | null = null;
  let unlocked = false;
  let muted = false;
  let sensory = false;
  let volumes: AudioVolumes = { master: 1, sfx: 0.8, music: 0.4, voice: 0.9 };
  const random = opts.random ?? Math.random;

  const applyGains = () => {
    if (!master || !sfxBus || !musicBus || !voiceBus) return;
    const ceiling = sensory ? SENSORY_CEILING : SFX_CEILING;
    const cap = Math.min(1, volumes.master) * (muted ? 0 : 1);
    master.gain.value = cap;
    sfxBus.gain.value = Math.min(ceiling, volumes.sfx) * cap;
    musicBus.gain.value = Math.min(0.6, volumes.music) * cap * (sensory ? 0 : 1);
    voiceBus.gain.value = Math.min(1, volumes.voice) * cap;
  };

  const ensureGraph = () => {
    if (ctx) return;
    ctx = opts.createContext();
    const anyCtx = ctx as unknown as { resume?: () => Promise<void>; suspend?: () => Promise<void> };
    controls = { resume: anyCtx.resume?.bind(anyCtx), suspend: anyCtx.suspend?.bind(anyCtx) };
    master = ctx.createGain();
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -10;
    limiter.knee.value = 6;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.15;
    sfxBus = ctx.createGain();
    musicBus = ctx.createGain();
    voiceBus = ctx.createGain();
    sfxBus.connect(master);
    musicBus.connect(master);
    voiceBus.connect(master);
    master.connect(limiter);
    limiter.connect(ctx.destination);
    applyGains();
  };

  return {
    unlock() {
      if (unlocked) return;
      ensureGraph();
      // The resume is the gesture-gated part: browsers create contexts suspended.
      void controls.resume?.();
      unlocked = true;
    },
    setVolumes(v) {
      volumes = v;
      applyGains();
    },
    setMuted(m) {
      muted = m;
      applyGains();
    },
    setSensoryFriendly(on) {
      sensory = on;
      if (on) ambient?.stop();
      applyGains();
    },
    play(id, pan = 0) {
      // The two hard gates from the spec: nothing sounds before a gesture or while muted.
      if (!unlocked || muted || !ctx || !sfxBus) return;
      // A few percent of random detune per play so repetition does not sound mechanical.
      playSfx(id, ctx, sfxBus, ctx.currentTime, { pan, detune: (random() * 2 - 1) * 0.5, random });
    },
    startAmbient(mood) {
      if (!unlocked || muted || sensory || !ctx || !musicBus) return;
      ambient?.stop();
      ambient = new AmbientVoice(ctx, musicBus, mood, random);
      ambient.start();
    },
    stopAmbient() {
      ambient?.stop();
      ambient = null;
    },
    handleVisibility(visible) {
      if (!ctx) return;
      if (visible && unlocked) void controls.resume?.();
      if (!visible) void controls.suspend?.();
    },
  };
}
